import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {object} from 'prop-types';
import React, {useRef, useState} from 'react';
import {useFeaturedPublications} from '../client.js';
import {formatDate, getUpdatedSearchUrl} from '../utils.js';
import Link from './Link.jsx';

function truncate(text, length, buffer) {
  if (text.length <= length) {
    return text;
  }
  const space = text.indexOf(' ', length);
  if (space === -1 || space > length + buffer) {
    return text.substring(0, length) + '...';
  }
  return text.substring(0, space) + '...';
}

function getFeaturedImageUrl(publication) {
  for (const link of publication.links) {
    if (link.rel === 'image') {
      return link.href;
    }
  }
  return '';
}

function FeaturedPublicationCard({publication}) {
  const imageUrl = getFeaturedImageUrl(publication);

  return (
    <Card
      sx={{
        minWidth: 320,
        maxWidth: 380,
        height: 320,
        marginRight: 2,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
        boxShadow: 1,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <CardMedia
        component="img"
        image={imageUrl}
        alt=""
        sx={{
          height: 120,
          objectFit: 'cover',
          backgroundColor: 'action.hover',
        }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: '16px !important',
        }}
      >
        <Typography
          variant="body2"
          sx={{marginBottom: 0.5, color: 'black.700'}}
        >
          {publication.authors[0].name} - {publication.publisher},{' '}
          {formatDate(publication.datePublished)}
        </Typography>

        <Link
          to={`/publications/${publication.id}`}
          sx={{marginBottom: 0.75, display: 'block', flex: 1, minHeight: 0}}
        >
          <Typography
            variant="h6"
            component="h6"
            sx={{
              lineHeight: 1.25,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {!publication.published && (
              <Chip
                label="draft"
                size="small"
                sx={{
                  marginRight: 1,
                  marginBottom: 0.5,
                  fontVariant: 'all-small-caps',
                }}
              />
            )}
            {truncate(publication.title, 160, 25)}
          </Typography>
        </Link>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            marginTop: 1,
            justifyContent: 'flex-start',
          }}
        >
          {publication.topics.slice(0, 3).map(topic => (
            <Chip
              key={topic}
              label={topic.toUpperCase()}
              component={Link}
              to={getUpdatedSearchUrl({topic})}
              clickable
              size="small"
              sx={{
                fontSize: '11px',
              }}
            />
          ))}
          {publication.topics.length > 3 && (
            <Typography
              variant="caption"
              sx={{color: 'text.secondary', alignSelf: 'center'}}
            >
              +{publication.topics.length - 3} more
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

FeaturedPublicationCard.propTypes = {
  publication: object.isRequired,
};

export default function FeaturedPublications() {
  const {data: response, error} = useFeaturedPublications();
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const featuredPublications = response?.data || [];

  const handleScroll = () => {
    if (scrollRef.current) {
      const {scrollLeft, scrollWidth, clientWidth} = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = direction => {
    if (scrollRef.current) {
      const scrollAmount = isMobile ? 340 : 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (error) {
    return null; // Silently fail if featured publications can't be loaded
  }

  if (!featuredPublications.length) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{marginBottom: 3, marginTop: 3}}>
      <Box sx={{position: 'relative'}}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            marginBottom: 2,
            fontWeight: 600,
            color: 'text.primary',
            marginLeft: 0,
          }}
        >
          Featured Publications
        </Typography>

        <Box sx={{position: 'relative'}}>
          {/* Left scroll arrow */}
          {!isMobile && showLeftArrow && (
            <IconButton
              onClick={() => scroll('left')}
              sx={{
                position: 'absolute',
                left: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'background.paper',
                  boxShadow: 3,
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* Carousel container */}
          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              paddingBottom: 1,
            }}
          >
            {featuredPublications.map(publication => (
              <FeaturedPublicationCard
                key={publication.id}
                publication={publication}
              />
            ))}
          </Box>

          {/* Right scroll arrow */}
          {!isMobile && showRightArrow && (
            <IconButton
              onClick={() => scroll('right')}
              sx={{
                position: 'absolute',
                right: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'background.paper',
                  boxShadow: 3,
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Container>
  );
}
